import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import apiService from '@/services/api'
import { toast } from 'sonner'

interface User {
  id: string
  nom: string
  prenom: string
  email: string
  telephone: string
  created_at?: string
  avatar?: string
  role?: string
  role_id?: number
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (identifier: string, password: string) => Promise<void>
  register: (data: any) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Retourne true si l'utilisateur a bien été chargé, false sinon.
  // Permet aux appelants (ProtectedRoute) de réessayer sur échec transitoire.
  const refreshUser = async (): Promise<boolean> => {
    try {
      // Essayer de récupérer le token depuis localStorage d'abord
      let token = localStorage.getItem('token')

      // Si pas de token dans localStorage, essayer sessionStorage
      if (!token) {
        token = sessionStorage.getItem('token')
        if (token) {
          localStorage.setItem('token', token)
        }
      }

      if (!token) {
        setUser(null)
        return false
      }

      // S'assurer que le token est bien chargé dans l'API service
      apiService.refreshToken()

      const userData = await apiService.getProfile()
      if (userData) {
        setUser(userData as User)
        return true
      } else {
        setUser(null)
        apiService.clearToken()
        return false
      }
    } catch (error) {
      console.error('Failed to fetch user:', error)
      // Ne déconnecter que si le serveur rejette explicitement le token (401/403).
      // Une erreur réseau (mobile money, réseau instable au retour de FedaPay)
      // ne doit JAMAIS détruire la session — sinon le client est déconnecté
      // et perd l'accès à ses tickets après paiement.
      const status = (error as Error & { status?: number })?.status
      if (status === 401 || status === 403) {
        setUser(null)
        apiService.clearToken()
      }
      // Erreur réseau / 5xx : on garde le token et l'état utilisateur actuel.
      return false
    }
  }

  useEffect(() => {
    const initAuth = async () => {
      // Essayer de récupérer le token avec plusieurs tentatives
      // Important après une redirection depuis FedaPay où le token peut ne pas être immédiatement disponible
      let token = localStorage.getItem('token')
      
      // Si pas de token dans localStorage, essayer sessionStorage
      if (!token) {
        token = sessionStorage.getItem('token')
        if (token) {
          localStorage.setItem('token', token)
        }
      }
      
      // Si toujours pas de token, attendre un peu et réessayer (max 2 secondes)
      if (!token) {
        const startTime = Date.now()
        const maxWaitTime = 2000
        const checkInterval = 200
        
        while (!token && Date.now() - startTime < maxWaitTime) {
          await new Promise(resolve => setTimeout(resolve, checkInterval))
          token = localStorage.getItem('token')
          if (!token) {
            token = sessionStorage.getItem('token')
            if (token) {
              localStorage.setItem('token', token)
            }
          }
        }
      }
      
      if (token) {
        try {
          await refreshUser()
        } catch (error) {
          // refreshUser gère déjà les erreurs en interne (401/403 → logout,
          // erreur réseau → session conservée). Ne rien détruire ici.
          console.error('Failed to initialize auth:', error)
        }
      }
      setIsLoading(false)
    }
    initAuth()
  }, [])

  // Rafraîchir automatiquement les données utilisateur toutes les 5 minutes
  useEffect(() => {
    if (user) {
      const interval = setInterval(() => {
        refreshUser()
      }, 5 * 60 * 1000) // 5 minutes
      
      return () => clearInterval(interval)
    }
  }, [user])

  const login = async (identifier: string, password: string) => {
    try {
      setIsLoading(true)
      const response = await apiService.login(identifier, password)
      // Le backend retourne le token et les infos utilisateur
      if (response.token) {
        apiService.setToken(response.token)
        setUser(response.user)
      }
      toast.success('Connexion réussie !')
    } catch (error: any) {
      toast.error(error.message || 'Échec de la connexion')
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const register = async (data: any) => {
    try {
      setIsLoading(true)
      const response = await apiService.register(data)
      // Le backend retourne le token et les infos utilisateur
      if (response.token) {
        apiService.setToken(response.token)
        setUser(response.user)
      }
      toast.success('Compte créé avec succès !')
    } catch (error: any) {
      toast.error(error.message || 'Échec de l\'inscription')
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const logout = () => {
    setUser(null)
    apiService.clearToken()
    toast.info('Déconnexion réussie')
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

